import React, { Component } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';

import { navigateTo } from '../utils/navigation';

var config = process.env;

// Panel de juegos usados: qué pasó con los usados y con el crédito que liberaron.
//
// Es HISTÓRICO COMPLETO, sin rango: el ciclo toma → venta → crédito → canje dura meses y cortarlo
// por fecha parte la historia. La evolución va en la serie mensual.
//
// Dos cosas se dicen en la cara y no en una nota al pie, porque cambian cómo se lee el número:
//  - de qué crédito sale cada gasto se decide por FIFO (el saldo vive por cliente, no por usado);
//  - dónde gana el local: el usado se vende al precio del crédito, así que venderlo no deja nada;
//    la ganancia es el margen de lo que el vendedor compra después con su crédito.
//
// GIFT CARDS (decisión del dueño, 2026-10-08): son la otra plata cobrada que todavía se debe en
// mercadería, así que viven en este mismo panel. `owed` junta todo lo que el local debe (saldo a
// favor de usados, el resto del saldo a favor y gift cards sin usar) y `giftCards` cuenta qué pasó
// con cada tarjeta vendida. Un backend anterior no manda ninguna de las dos claves: en ese caso
// no se muestra nada nuevo.

const money = (v) => (v == null ? '—' : `$${Math.round(Number(v)).toLocaleString('de-DE')}`);
const number = (v) => (v == null ? '—' : Number(v).toLocaleString('de-DE'));
const percent = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
const dias = (v) => (v == null ? '—' : `${Math.round(Number(v))} días`);

const AZUL = '#007bff';
const VERDE = '#17c671';

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const rotuloMes = (m) => {
    const [y, mm] = String(m).split('-');
    return `${MESES[Number(mm) - 1] || mm} ${String(y).slice(2)}`;
};

function Tile({ label, value, hint }) {
    return (
        <div className="col-lg col-md-4 col-sm-6 mb-4">
            <div className="stats-small stats-small--1 card card-small h-100">
                <div className="card-body px-3 py-3">
                    <div className="text-uppercase text-muted" style={{ fontSize: 11, letterSpacing: '.05em' }}>{label}</div>
                    <div style={{ fontSize: 24, fontWeight: 600, lineHeight: 1.3 }}>{value}</div>
                    {hint && <div className="text-muted" style={{ fontSize: 12 }}>{hint}</div>}
                </div>
            </div>
        </div>
    );
}

// Una distribución de tiempos: mediana como número principal y N al lado. Con pocos usados un
// promedio es una anécdota; la mediana aguanta el caso raro y el N dice cuánto creerle.
function Tiempo({ d }) {
    if (!d || !d.n) return <span className="text-muted">sin datos</span>;
    return (
        <span>
            <strong>{dias(d.median)}</strong>
            <span className="text-muted" style={{ fontSize: 12 }}> · p75 {dias(d.p75)} · máx {dias(d.max)} · N={d.n}</span>
        </span>
    );
}

function Card({ title, subtitle, children, bodyClass = 'p-0' }) {
    return (
        <div className="card card-small mb-4">
            <div className="card-header border-bottom">
                <h6 className="m-0">{title}</h6>
                {subtitle && <span className="text-muted" style={{ fontSize: 12 }}>{subtitle}</span>}
            </div>
            <div className={`card-body ${bodyClass}`}>{children}</div>
        </div>
    );
}

function Fila({ label, value, strong, muted, indent }) {
    return (
        <tr className={muted ? 'text-muted' : ''}>
            <td style={{ paddingLeft: indent ? 32 : undefined }}>{label}</td>
            <td className={`text-right ${strong ? 'font-weight-bold' : ''}`}>{value}</td>
        </tr>
    );
}

class UsedGamesAnalyticsComponent extends Component {
    constructor(props) {
        super(props);
        this.state = { loaded: false, error: null, data: null };
    }

    componentDidMount() { this.cargar(); }

    cargar = () => {
        this.setState({ loaded: false, error: null });
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}analytics/used-games`, {
            method: 'GET',
            headers: { 'access-token': localStorage.getItem('token'), 'Content-Type': 'application/json' },
        })
            .then((r) => r.json())
            .then((body) => {
                if (body.error > 0) {
                    if (body.error === 1002) { window.location.href = localStorage.getItem('homePageURL'); return; }
                    this.setState({ loaded: true, error: body.message, data: null });
                    return;
                }
                this.setState({ loaded: true, data: body.data, error: null });
            })
            .catch((e) => this.setState({ loaded: true, error: e.message, data: null }));
    };

    // ── Plata por canjear ─────────────────────────────────────────────────────────────────────
    //
    // Todo lo que el local ya cobró y todavía debe en mercadería, junto. Va arriba de todo porque
    // es la pregunta que el panel contesta primero: "¿cuánto debo?". Las tres partes no se pisan:
    // el sobrante o el canje de una gift card deja de ser "gift card sin usar" y pasa a ser saldo
    // a favor del cliente.
    renderPorCanjear() {
        const { owed } = this.state.data;
        if (!owed) return null;
        return (
            <Card
                title="Plata por canjear"
                subtitle="Lo que ya se cobró y todavía se debe en mercadería: saldo a favor de los clientes y gift cards sin usar."
            >
                <table className="table table-sm mb-0">
                    <tbody>
                        <Fila label="Saldo a favor de usados (crédito de usados vendidos, sin canjear)" value={money(owed.usedGamesCredit)} indent />
                        <Fila label="Otro saldo a favor (ajustes, sobrantes y canjes de gift cards)" value={money(owed.otherCredit)} indent />
                        <Fila label="Gift cards sin usar" value={money(owed.giftCardsPending)} indent />
                        <tr className="bg-light">
                            <td className="font-weight-bold">Total por canjear</td>
                            <td className="text-right font-weight-bold" style={{ fontSize: 18 }}>{money(owed.total)}</td>
                        </tr>
                    </tbody>
                </table>
            </Card>
        );
    }

    // ── Gift cards ────────────────────────────────────────────────────────────────────────────
    //
    // Qué pasó con cada peso de gift card vendido. Las cuatro filas de destino suman lo vendido
    // (usado + pasado a saldo + vencido + pendiente). Las ANULADAS van aparte y no entran en esa
    // cuenta: su venta se deshizo, esa plata no se cobró.
    renderGiftCards() {
        const gc = this.state.data.giftCards;
        if (!gc) return null;
        const bloque = (b) => b || {};
        const sold = bloque(gc.sold);
        const used = bloque(gc.used);
        const redeemed = bloque(gc.redeemed);
        const pending = bloque(gc.pending);
        const expired = bloque(gc.expired);
        const cancelled = bloque(gc.cancelled);
        const tarjetas = (n) => `${number(n || 0)} tarjeta(s)`;
        const meses = (gc.monthly || []).map((m) => ({ ...m, label: rotuloMes(m.month) }));

        return (
            <Card
                title="Gift cards"
                subtitle="Vender una gift card no es ingreso todavía: es plata cobrada que se debe en mercadería. La ganancia está en lo que se compra con ella."
            >
                {!sold.count ? (
                    <div className="text-center text-muted py-4">Todavía no se vendió ninguna gift card.</div>
                ) : (
                    <div className="row no-gutters">
                        <div className={meses.length > 0 ? 'col-lg-7 border-right' : 'col-12'}>
                            <table className="table table-sm mb-0">
                                <tbody>
                                    <Fila label={`Vendidas · ${tarjetas(sold.count)}`} value={money(sold.amount)} strong />
                                    <Fila label={`Usadas en compras · ${tarjetas(used.count)} (lo aplicado a la compra)`} value={money(used.applied)} indent />
                                    <Fila
                                        label={`Pasado a saldo a favor: ${money(used.leftoverToCredit)} de sobrantes + ${money(redeemed.amount)} de ${number(redeemed.count || 0)} canjeada(s)`}
                                        value={money(gc.movedToCredit)}
                                        indent
                                    />
                                    <Fila label={`Pendiente de canje · ${tarjetas(pending.count)} (se deben en mercadería)`} value={money(pending.amount)} indent strong />
                                    <Fila label={`Vencidas sin usar · ${tarjetas(expired.count)} (ya no se deben)`} value={money(expired.amount)} indent />
                                    <Fila label={`Anuladas · ${tarjetas(cancelled.count)} (su venta se deshizo: no cuentan como vendidas)`} value={money(cancelled.amount)} muted />
                                </tbody>
                            </table>
                        </div>
                        {meses.length > 0 && (
                            <div className="col-lg-5">
                                <table className="table table-sm mb-0">
                                    <thead className="bg-light">
                                        <tr><th className="border-0">Mes</th><th className="border-0 text-right">Vendidas</th><th className="border-0 text-right">Monto</th></tr>
                                    </thead>
                                    <tbody>
                                        {[...meses].reverse().map((m) => (
                                            <tr key={m.month}>
                                                <td>{m.label}</td>
                                                <td className="text-right">{number(m.count)}</td>
                                                <td className="text-right">{money(m.sold)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </Card>
        );
    }

    renderResumen() {
        const { inventory, sales, credit, economics } = this.state.data;
        return (
            <div className="row">
                <Tile label="Usados tomados" value={number(inventory.total)}
                    hint={`${number(inventory.byState.available || 0)} disponibles · ${number(inventory.byState.withdrawn || 0)} retirados`} />
                <Tile label="Vendidos" value={number(sales.units)} hint={`por ${money(sales.revenue)}`} />
                <Tile label="Crédito liberado" value={money(credit.released)} hint={`a ${number(credit.creditsCount)} ventas de usados`} />
                <Tile label="Crédito canjeado" value={money(credit.spentInPurchases)} hint={`${percent(credit.spentPct)} de lo liberado`} />
                <Tile label="Crédito sin usar" value={money(credit.outstanding)} hint={`${number(credit.clientsWithBalance)} cliente(s) con saldo`} />
                <Tile label="Ganancia a hoy" value={money(economics.realizedGain)} hint={`margen de lo canjeado: ${percent(economics.gainOnRedeemedPct)}`} />
            </div>
        );
    }

    renderEconomia() {
        const { economics, spending } = this.state.data;
        return (
            <Card
                title="Dónde ganamos: lo que los vendedores compran con su crédito"
                subtitle={<>Vender el usado no deja nada (se vende al precio del crédito que se le da al vendedor). La ganancia es el margen de lo que el vendedor se lleva de la tienda con ese crédito: lo paga a precio de venta y a nosotros nos cuesta el costo.</>}
            >
                <table className="table table-sm mb-0">
                    <tbody>
                        <Fila label="Crédito canjeado (lo que se llevaron, a precio de venta)" value={money(economics.creditRedeemed)} />
                        <Fila label="Costo de esa mercadería" value={`− ${money(economics.creditRedeemedCost)}`} indent muted />
                        <Fila label="Ganancia por el canje" value={`${money(economics.gainOnRedeemed)} (${percent(economics.gainOnRedeemedPct)})`} strong />
                        {Math.round(economics.priceOverCredit || 0) !== 0 && (
                            <Fila
                                label={`Diferencia entre precio de venta y crédito de los usados vendidos (${money(economics.saleRevenue)} − ${money(economics.creditReleased)})`}
                                value={money(economics.priceOverCredit)}
                            />
                        )}
                        <Fila label="Ganancia de los usados a hoy" value={money(economics.realizedGain)} strong />
                        <Fila label="Crédito todavía sin canjear (ya cobrado, se debe en mercadería)" value={money(economics.creditOutstanding)} />
                        <Fila
                            label={`Ganancia que dejaría ese saldo al canjearse, al margen de hoy (${percent(economics.gainOnRedeemedPct)})`}
                            value={economics.expectedGainOnOutstanding == null ? '—' : `≈ ${money(economics.expectedGainOnOutstanding)}`}
                            indent muted
                        />
                        <Fila label="Además: margen del resto de esas compras (lo que no pagó el crédito de usados)" value={money(economics.marginOnRestOfPurchases)} />
                    </tbody>
                </table>
                {(spending.linesWithoutCost > 0 || spending.excluded.replacement + spending.excluded.cancelled + spending.excluded.missing > 0) && (
                    <div className="px-3 py-2 text-muted border-top" style={{ fontSize: 12 }}>
                        {spending.linesWithoutCost > 0 && <div>⚠ {number(spending.linesWithoutCost)} línea(s) compradas con crédito no tienen costo cargado: la ganancia está calculada de más.</div>}
                        {spending.excluded.replacement > 0 && <div>{number(spending.excluded.replacement)} compra(s) con crédito eran cambios en reemplazo: no entran en la ganancia.</div>}
                        {spending.excluded.cancelled > 0 && <div>{number(spending.excluded.cancelled)} compra(s) canceladas se pagaron con crédito que no se devolvió.</div>}
                        {spending.excluded.missing > 0 && <div>{number(spending.excluded.missing)} pago(s) con crédito apuntan a una compra que no existe.</div>}
                    </div>
                )}
            </Card>
        );
    }

    renderTiempos() {
        const { inventory, sales, credit } = this.state.data;
        return (
            <Card title="Tiempos" subtitle="Mediana, con el percentil 75, el máximo y sobre cuántos casos (N).">
                <table className="table table-sm mb-0">
                    <tbody>
                        <tr><td>De la toma a la venta</td><td className="text-right"><Tiempo d={sales.daysToSell} /></td></tr>
                        <tr><td>Disponibles hoy: cuánto llevan en el local</td><td className="text-right"><Tiempo d={inventory.available.daysOnShelf} /></td></tr>
                        <tr><td>De liberado el crédito al primer canje</td><td className="text-right"><Tiempo d={credit.daysToFirstUse} /></td></tr>
                        <tr><td>De liberado el crédito a gastarlo entero</td><td className="text-right"><Tiempo d={credit.daysToExhaust} /></td></tr>
                        <tr className="text-muted">
                            <td>Créditos que todavía no se tocaron</td>
                            <td className="text-right">{number(credit.neverUsedCredits)} de {number(credit.creditsCount)}</td>
                        </tr>
                    </tbody>
                </table>
            </Card>
        );
    }

    renderCredito() {
        const { credit, giftCards } = this.state.data;
        // Con gift cards, "lo que no es crédito de usados" ya no son sólo ajustes: también los
        // sobrantes y los canjes de gift cards entran al saldo a favor.
        const otrasFuentes = giftCards ? 'ajustes manuales, sobrantes y canjes de gift cards' : 'ajustes manuales';
        const total = credit.outstandingAging.reduce((s, t) => s + (t.amount || 0), 0);
        return (
            <Card
                title="El crédito sin usar, por antigüedad"
                subtitle="Cuánto hace que se liberó lo que todavía se debe."
            >
                <table className="table table-sm mb-0">
                    <thead className="bg-light">
                        <tr><th className="border-0">Antigüedad</th><th className="border-0 text-right">Créditos</th><th className="border-0 text-right">Saldo</th><th className="border-0" style={{ width: '35%' }}>&nbsp;</th></tr>
                    </thead>
                    <tbody>
                        {credit.outstandingAging.map((t) => (
                            <tr key={t.label}>
                                <td>{t.label}</td>
                                <td className="text-right">{number(t.count)}</td>
                                <td className="text-right font-weight-bold">{money(t.amount)}</td>
                                <td>
                                    <div style={{ background: '#e9ecef', borderRadius: 3, height: 14 }}>
                                        <div style={{ width: `${total > 0 ? Math.round((t.amount / total) * 100) : 0}%`, background: AZUL, height: '100%', borderRadius: 3 }} />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="px-3 py-2 text-muted border-top" style={{ fontSize: 12 }}>
                    Aparte del crédito de usados: {money(credit.otherSources.added)} entraron por {otrasFuentes} ({money(credit.otherSources.outstanding)} sin usar)
                    {credit.writtenOff > 0 && <> · {money(credit.writtenOff)} de crédito de usados se dio de baja con ajustes negativos</>}
                    {credit.unfunded > 0 && <> · ⚠ {money(credit.unfunded)} se gastaron sin crédito que los respalde (el saldo quedó negativo)</>}.
                </div>
            </Card>
        );
    }

    renderGasto() {
        const { spending } = this.state.data;
        const maxRubro = Math.max(...spending.byCategory.map((r) => r.credit || 0), 1);
        return (
            <Card
                title="En qué se usa el crédito"
                subtitle={<>{number(spending.purchases)} compra(s) usaron crédito de usados: {money(spending.creditFromUsed)} de crédito y <strong>{money(spending.cashOnTop)} pagados encima</strong>. El margen de esas compras fue {money(spending.margin)} ({percent(spending.marginPct)}); la parte que pagó el crédito de usados se llevó {money(spending.attributedMargin)}.</>}
            >
                <div className="row no-gutters">
                    <div className="col-lg-6 border-right">
                        <table className="table table-sm mb-0">
                            <thead className="bg-light">
                                <tr><th className="border-0">Rubro</th><th className="border-0 text-right">Unid.</th><th className="border-0 text-right">Crédito</th><th className="border-0" style={{ width: '30%' }}>&nbsp;</th></tr>
                            </thead>
                            <tbody>
                                {spending.byCategory.length === 0 && (
                                    <tr><td colSpan="4" className="text-center text-muted py-3">Todavía nadie canjeó crédito de usados.</td></tr>
                                )}
                                {spending.byCategory.map((r) => (
                                    <tr key={r.category}>
                                        <td>{r.category}</td>
                                        <td className="text-right">{number(r.units)}</td>
                                        <td className="text-right font-weight-bold">{money(r.credit)}</td>
                                        <td>
                                            <div style={{ background: '#e9ecef', borderRadius: 3, height: 14 }}>
                                                <div style={{ width: `${Math.round(((r.credit || 0) / maxRubro) * 100)}%`, background: VERDE, height: '100%', borderRadius: 3 }} />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <table className="table table-sm mb-0 border-top">
                            <thead className="bg-light">
                                <tr><th className="border-0">Dónde se canjea</th><th className="border-0 text-right">Compras</th><th className="border-0 text-right">Crédito</th><th className="border-0 text-right">Pagado encima</th></tr>
                            </thead>
                            <tbody>
                                {spending.byOrigin.map((o) => (
                                    <tr key={o.origin}>
                                        <td>{o.origin}</td>
                                        <td className="text-right">{number(o.purchases)}</td>
                                        <td className="text-right">{money(o.credit)}</td>
                                        <td className="text-right">{money(o.cashOnTop)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="col-lg-6">
                        <table className="table table-sm mb-0">
                            <thead className="bg-light">
                                <tr><th className="border-0">Lo más comprado con crédito</th><th className="border-0 text-right">Unid.</th><th className="border-0 text-right">Crédito</th></tr>
                            </thead>
                            <tbody>
                                {spending.topProducts.length === 0 && (
                                    <tr><td colSpan="3" className="text-center text-muted py-3">—</td></tr>
                                )}
                                {spending.topProducts.map((p) => (
                                    <tr key={p.code}>
                                        <td>{p.name || `#${p.code}`}<div className="text-muted" style={{ fontSize: 11 }}>{p.category}</div></td>
                                        <td className="text-right">{number(p.units)}</td>
                                        <td className="text-right">{money(p.credit)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Card>
        );
    }

    renderVentas() {
        const { sales, inventory } = this.state.data;
        return (
            <div className="row">
                <div className="col-lg-6">
                    <Card title="Por dónde se venden" subtitle={`Precio contra el nuevo: ${percent(inventory.discountVsNewPct.median)} más barato (mediana, N=${number(inventory.discountVsNewPct.n)}).`}>
                        <table className="table table-sm mb-0">
                            <thead className="bg-light">
                                <tr><th className="border-0">Canal</th><th className="border-0 text-right">Vendidos</th><th className="border-0 text-right">Ingreso</th><th className="border-0 text-right">Crédito liberado</th></tr>
                            </thead>
                            <tbody>
                                {sales.byOrigin.length === 0 && (
                                    <tr><td colSpan="4" className="text-center text-muted py-3">Todavía no se vendió ningún usado.</td></tr>
                                )}
                                {sales.byOrigin.map((o) => (
                                    <tr key={o.origin}>
                                        <td>{o.origin}</td>
                                        <td className="text-right">{number(o.units)}</td>
                                        <td className="text-right">{money(o.revenue)}</td>
                                        <td className="text-right">{money(o.credit)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </div>
                <div className="col-lg-6">
                    <Card title="Los que más tiempo llevan en el local" subtitle={`Disponibles: ${number(inventory.available.count)} por ${money(inventory.available.listValue)} de precio, con ${money(inventory.available.pendingCredit)} de crédito comprometido si se venden.`}>
                        <table className="table table-sm mb-0">
                            <thead className="bg-light">
                                <tr><th className="border-0">Juego</th><th className="border-0 text-right">Días</th><th className="border-0 text-right">Precio</th><th className="border-0 text-right">Crédito</th></tr>
                            </thead>
                            <tbody>
                                {inventory.available.stale.length === 0 && (
                                    <tr><td colSpan="4" className="text-center text-muted py-3">No hay usados disponibles.</td></tr>
                                )}
                                {inventory.available.stale.map((g) => (
                                    <tr key={g.code}>
                                        <td><a href={`/editUsedGame?code=${g.code}`} onClick={(e) => { e.preventDefault(); navigateTo(`/editUsedGame?code=${g.code}`); }}>{g.name}</a></td>
                                        <td className="text-right">{number(g.days)}</td>
                                        <td className="text-right">{money(g.price)}</td>
                                        <td className="text-right">{money(g.agreedCredit)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </div>
            </div>
        );
    }

    renderVendedores() {
        const { sellers } = this.state.data;
        return (
            <Card title="Quiénes traen usados" subtitle={`${number(sellers.count)} vendedor(es); ${number(sellers.repeat)} trajeron más de uno.`}>
                <table className="table table-sm mb-0">
                    <thead className="bg-light">
                        <tr><th className="border-0">Vendedor</th><th className="border-0 text-right">Juegos</th><th className="border-0 text-right">Vendidos</th><th className="border-0 text-right">Crédito liberado</th></tr>
                    </thead>
                    <tbody>
                        {sellers.top.map((s) => (
                            <tr key={s.cedula}>
                                <td>
                                    <a href={`/clientDetail?cedula=${encodeURIComponent(s.cedula)}`} onClick={(e) => { e.preventDefault(); navigateTo(`/clientDetail?cedula=${encodeURIComponent(s.cedula)}`); }}>
                                        {s.name || s.cedula}
                                    </a>
                                    {s.name && <span className="text-muted" style={{ fontSize: 11 }}> · CI {s.cedula}</span>}
                                </td>
                                <td className="text-right">{number(s.games)}</td>
                                <td className="text-right">{number(s.sold)}</td>
                                <td className="text-right">{money(s.credit)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>
        );
    }

    renderMensual() {
        const { monthly } = this.state.data;
        const datos = monthly.map((m) => ({ ...m, label: rotuloMes(m.month) }));
        const tooltipMoney = (v) => money(v);
        return (
            <Card title="Mes a mes" subtitle="Tomas y ventas por el mes en que ocurrieron; el crédito por el mes en que se liberó o se canjeó." bodyClass="p-0">
                {datos.length === 0 ? (
                    <div className="text-center text-muted py-4">Sin movimientos todavía.</div>
                ) : (
                    <div>
                        <div className="row no-gutters">
                            {/* Dos gráficos y no uno con dos ejes: unidades y pesos no comparten escala. */}
                            <div className="col-lg-6 p-3">
                                <div className="text-muted mb-2" style={{ fontSize: 12 }}>Usados tomados y vendidos (unidades)</div>
                                <ResponsiveContainer width="100%" height={220}>
                                    <BarChart data={datos} barGap={2}>
                                        <CartesianGrid vertical={false} stroke="#eef0f2" />
                                        <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={30} />
                                        <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                                        <Legend iconType="square" wrapperStyle={{ fontSize: 12 }} />
                                        <Bar dataKey="intakes" name="Tomados" fill={AZUL} radius={[4, 4, 0, 0]} />
                                        <Bar dataKey="sold" name="Vendidos" fill={VERDE} radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="col-lg-6 p-3">
                                <div className="text-muted mb-2" style={{ fontSize: 12 }}>Crédito liberado y canjeado ($)</div>
                                <ResponsiveContainer width="100%" height={220}>
                                    <BarChart data={datos} barGap={2}>
                                        <CartesianGrid vertical={false} stroke="#eef0f2" />
                                        <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                                        <YAxis tickFormatter={(v) => `$${Number(v).toLocaleString('de-DE')}`} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={60} />
                                        <Tooltip formatter={tooltipMoney} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                                        <Legend iconType="square" wrapperStyle={{ fontSize: 12 }} />
                                        <Bar dataKey="creditReleased" name="Liberado" fill={AZUL} radius={[4, 4, 0, 0]} />
                                        <Bar dataKey="creditSpent" name="Canjeado" fill={VERDE} radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                        <table className="table table-sm mb-0 border-top">
                            <thead className="bg-light">
                                <tr>
                                    <th className="border-0">Mes</th>
                                    <th className="border-0 text-right">Tomados</th>
                                    <th className="border-0 text-right">Vendidos</th>
                                    <th className="border-0 text-right">Ingreso por ventas</th>
                                    <th className="border-0 text-right">Crédito liberado</th>
                                    <th className="border-0 text-right">Crédito canjeado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...datos].reverse().map((m) => (
                                    <tr key={m.month}>
                                        <td>{m.label}</td>
                                        <td className="text-right">{number(m.intakes)}</td>
                                        <td className="text-right">{number(m.sold)}</td>
                                        <td className="text-right">{money(m.saleRevenue)}</td>
                                        <td className="text-right">{money(m.creditReleased)}</td>
                                        <td className="text-right">{money(m.creditSpent)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        );
    }

    render() {
        const { loaded, error, data } = this.state;
        if (!loaded) return <div className="text-center text-muted py-5">Cargando…</div>;
        if (error) return <div className="alert alert-danger">No se pudo cargar el panel: {error}</div>;
        if (!data) return null;

        return (
            <div>
                <p className="text-muted" style={{ fontSize: 13 }}>
                    Todo el historial de los usados{data.giftCards ? ' y de las gift cards' : ''}. El crédito vive por cliente y no por usado, así que cuando un
                    cliente gasta, se considera que usa primero su crédito <strong>más viejo</strong> (FIFO); el
                    que vino de ajustes manuales se cuenta aparte. Las ventas revertidas y las compras canceladas
                    con el crédito devuelto no suman.
                </p>
                {this.renderPorCanjear()}
                {this.renderResumen()}
                {this.renderEconomia()}
                {this.renderGiftCards()}
                <div className="row">
                    <div className="col-lg-6">{this.renderTiempos()}</div>
                    <div className="col-lg-6">{this.renderCredito()}</div>
                </div>
                {this.renderGasto()}
                {this.renderVentas()}
                {this.renderMensual()}
                {this.renderVendedores()}
            </div>
        );
    }
}

export default UsedGamesAnalyticsComponent;
